import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SocialEngagement = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const socialPlatforms = [
    {
      name: "Instagram",
      icon: "Instagram",
      handle: "@mavacold",
      followers: "25.2K",
      color: "from-pink-500 to-purple-600",
      description: "Daily hydration tips & lifestyle content"
    },
    {
      name: "Facebook",
      icon: "Facebook",
      handle: "Mava Cold Beverages",
      followers: "18.5K",
      color: "from-blue-600 to-blue-800",
      description: "Community updates & product launches"
    },
    {
      name: "Twitter",
      icon: "Twitter",
      handle: "@MavaCold",
      followers: "12.8K",
      color: "from-sky-400 to-blue-500",
      description: "Sustainability news & quick tips"
    },
    {
      name: "YouTube",
      icon: "Youtube",
      handle: "Mava Cold Beverages",
      followers: "8.9K",
      color: "from-red-500 to-red-700",
      description: "Product reviews & hydration science"
    }
  ];

  const handleNewsletterSubmit = (e) => {
    e?.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setTimeout(() => {
        setEmail('');
        setIsSubscribed(false);
      }, 3000);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOutBack"
      }
    }
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-poppins font-bold text-primary mb-4">
            Stay Connected with Mava
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Join our community for exclusive updates, hydration tips, and be the first to know about new products
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Social Media Section */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-poppins font-semibold text-primary mb-6 text-center lg:text-left">
              Follow Us on Social Media
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {socialPlatforms?.map((platform) => (
                <motion.div
                  key={platform?.name}
                  className="group cursor-pointer"
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="bg-card border border-border rounded-lg p-4 h-full liquid-shadow hover:shadow-lg brand-transition">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-10 h-10 bg-gradient-to-br ${platform?.color} rounded-lg flex items-center justify-center group-hover:scale-110 brand-transition`}>
                        <Icon name={platform?.icon} size={20} color="white" />
                      </div>
                      <div>
                        <h4 className="font-poppins font-semibold text-primary">
                          {platform?.name}
                        </h4>
                        <p className="text-sm text-text-secondary">
                          {platform?.followers} followers
                        </p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-text-primary mb-3">
                      {platform?.description}
                    </p>
                    
                    <p className="text-sm font-medium text-accent">
                      {platform?.handle}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Social Sharing */}
            <motion.div
              className="mt-8 bg-card border border-border rounded-lg p-6 liquid-shadow"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              <h4 className="font-poppins font-semibold text-primary mb-4 text-center">
                Share Your Mava Experience
              </h4>
              <div className="flex justify-center space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Share2"
                  iconPosition="left"
                  className="water-ripple"
                >
                  Share
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Heart"
                  iconPosition="left"
                  className="water-ripple"
                >
                  Like
                </Button>
              </div>
            </motion.div>
          </motion.div>

          {/* Newsletter Section */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <div className="bg-card border border-border rounded-xl p-8 liquid-shadow">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-accent to-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Mail" size={28} color="white" />
                </div>
                <h3 className="text-2xl font-poppins font-semibold text-primary mb-2">
                  Join Our Newsletter
                </h3>
                <p className="text-text-secondary">
                  Get exclusive hydration tips, product updates, and special offers delivered to your inbox
                </p>
              </div>

              {!isSubscribed ? (
                <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                  <Input
                    type="email"
                    label="Email Address"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    required
                    className="water-ripple"
                  />
                  
                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    iconName="Send"
                    iconPosition="right"
                    className="bg-action hover:bg-action/90 water-ripple"
                  >
                    Subscribe Now
                  </Button>
                </form>
              ) : (
                <motion.div
                  className="text-center py-8"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, ease: "easeOutBack" }}
                >
                  <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Check" size={28} color="white" strokeWidth={3} />
                  </div>
                  <h4 className="text-xl font-poppins font-semibold text-success mb-2">
                    Welcome to Mava Family!
                  </h4>
                  <p className="text-text-secondary">
                    You'll receive our first newsletter within 24 hours
                  </p>
                </motion.div>
              )}

              {/* Newsletter Benefits */}
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm font-medium text-primary mb-3">What you'll get:</p>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li className="flex items-center space-x-2">
                    <Icon name="Check" size={14} color="var(--color-success)" />
                    <span>Weekly hydration tips from experts</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="Check" size={14} color="var(--color-success)" />
                    <span>Exclusive product launches & offers</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="Check" size={14} color="var(--color-success)" />
                    <span>Sustainability updates & impact reports</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SocialEngagement;